import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  ClipboardDocumentListIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import Footer from "../../components/layout/Footer";
import Header from "../../components/layout/Header";
import LoadingScreen from "../../components/loadingScreen";
import { useUserContext } from "../../hooks/useUserContext";
import DateRangeSelector from "./components/dateRangeSelector";
import OrderCard from "./components/orderCard";
import SearchBar from "./components/searchBar";
import StatusSelector from "./components/statusSelector";
import {
  getPeriodRange,
  DEFAULT_PERIOD,
} from "./components/dateRangeSelector/dateRanges";
import { getOrders } from "../../api/orderService";
import type {
  Order,
  OrderStepValue,
} from "./components/statusSelector/orderSteps";
import AddNewOrderModal from "../../components/modals/AddNewOrderModal";

export default function OrdersPage() {
  const { isLoading } = useUserContext();
  const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const dateRange = getPeriodRange(period);
  const [status, setStatus] = useState<OrderStepValue | null>(null);

  const { data } = useQuery({
    queryKey: [
      "orders",
      {
        search: searchTerm,
        period,
        status,
      },
    ],
    queryFn: () =>
      getOrders({
        search: searchTerm,
        startTime: dateRange.from,
        endTime: dateRange.to,
        status: status ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });
  const orders = Array.isArray(data) ? data : [];
  const isFilterActive = !!(searchTerm || status);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FAFB] font-sans">
      <Header />
      <main className="flex-1 px-4 md:px-12 lg:px-30 py-6 md:py-5">
        <div className="flex flex-col gap-6 md:gap-8 md:p-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <h1 className="text-2xl md:text-3xl font-medium text-gray-950">
                Orders
              </h1>
              <p className="text-sm text-gray-400">Manage your orders</p>
            </div>
            <button
              type="button"
              onClick={() => setIsAddOrderModalOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 md:px-5 py-2.5 text-sm font-medium text-white cursor-pointer"
            >
              <PlusIcon className="size-5" />
              New order
            </button>
          </div>

          <div className="flex flex-col gap-5">
            <div className="flex flex-col md:flex-row gap-3 md:gap-5">
              <SearchBar onSearch={setSearchTerm} />

              <div className="flex gap-3 md:gap-5">
                <DateRangeSelector
                  period={period}
                  onPeriodChange={(newPeriod) => setPeriod(newPeriod)}
                />
                <StatusSelector
                  status={status}
                  onStatusChange={(newStatus) => setStatus(newStatus)}
                />
              </div>
            </div>

            {orders.length > 0 ? (
              orders.map((order: Order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                <div className="flex size-20 items-center justify-center rounded-2xl bg-gray-100">
                  <ClipboardDocumentListIcon className="size-12 text-gray-400" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-gray-950">
                  {isFilterActive
                    ? "No matching orders found"
                    : "No orders found"}
                </h3>
                <p className="mt-2 max-w-xs text-base text-gray-500">
                  {isFilterActive
                    ? "We couldn't find any orders matching your search or filter criteria."
                    : "You don't have any orders yet. Once an order is created, it will appear here."}
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      <AddNewOrderModal
        isOpen={isAddOrderModalOpen}
        onClose={() => setIsAddOrderModalOpen(false)}
      />
    </div>
  );
}
